/**
 * Section B7.6 Page - B.7.6.事前排放減量總和
 * Summary of ex ante estimates of emission reductions/net removals
 * UI 模式：單頁可編輯摘要表格
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import sectionB76Service, { SectionB76Detail, SummaryRow, importFromXlsx } from '../services/sectionB76Service';
import FunctionPageHeader from '../components/FunctionPageHeader';

// ── UUID 產生 ─────────────────────────────────────────────────────
function genId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// ── 儲存回饋 ──────────────────────────────────────────────────────
function useSaveFeedback() {
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const show = (ok: boolean, text: string) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 2500);
  };
  return { msg, show };
}

// ── 數值格式化 ────────────────────────────────────────────────────
function fmtNum(v: number | null | undefined): string {
  if (v == null) return '';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseNum(s: string): number | null {
  if (s === '' || s === null || s === undefined) return null;
  const n = parseFloat(s.replace(/,/g, ''));
  return isNaN(n) ? null : n;
}

// ── 起始年從日期字串取出 ─────────────────────────────────────────
function extractYear(ddmmyyyy: string): number | null {
  if (!ddmmyyyy) return null;
  const parts = ddmmyyyy.split('/');
  if (parts.length !== 3) return null;
  const y = parseInt(parts[2]);
  return isNaN(y) ? null : y;
}

// ── 自動產生年度列 ────────────────────────────────────────────────
function generateRows(creditingPeriodStart: string | null, totalYears: number): SummaryRow[] {
  const startYear = extractYear(creditingPeriodStart ?? '');
  return Array.from({ length: totalYears }, (_, i) => ({
    id: genId(),
    year_label: startYear ? `Year ${i + 1} (${startYear + i})` : `Year ${i + 1}`,
    baseline_emissions:  null,
    project_emissions:   null,
    leakage_emissions:   null,
    emission_reductions: null,
  }));
}

// ════════════════════════════════════════════════════════════════
// NumberCell - 可編輯數字格
// ════════════════════════════════════════════════════════════════
interface NumberCellProps {
  value: number | null;
  onChange: (v: number | null) => void;
  disabled?: boolean;
  highlighted?: boolean;  // emission_reductions 覆寫鎖定
}
const NumberCell: React.FC<NumberCellProps> = ({ value, onChange, disabled, highlighted }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = () => {
    if (disabled) return;
    setDraft(value != null ? String(value) : '');
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 10);
  };

  const commit = () => {
    onChange(parseNum(draft));
    setEditing(false);
  };

  if (editing) {
    return (
      <input ref={inputRef} type="number" step="0.01" value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        style={{ width: '100%', padding: '3px 6px', border: '1px solid #4A90E2', borderRadius: 4, fontSize: 12, textAlign: 'right', boxSizing: 'border-box' }} />
    );
  }

  return (
    <div onClick={startEdit}
      style={{ cursor: disabled ? 'default' : 'pointer', textAlign: 'right', padding: '4px 6px', fontSize: 12,
        background: highlighted ? '#fff7e6' : 'transparent', borderRadius: 4, minHeight: 22,
        color: value == null ? '#ccc' : '#222' }}>
      {value != null ? fmtNum(value) : (disabled ? '—' : <span style={{ color: '#d0d0d0', fontSize: 11 }}>click to edit</span>)}
      {highlighted && <span style={{ marginLeft: 4, fontSize: 10 }}>🔒</span>}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Main Page
// ════════════════════════════════════════════════════════════════
const SectionB76Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { selectedProjectId } = useProject();
  const { hasPermission } = usePermission();
  const canUpdate = hasPermission('section_b7_6', 'update');
  const { msg, show } = useSaveFeedback();

  const [detail, setDetail]           = useState<SectionB76Detail | null>(null);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [importing, setImporting]     = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const xlsxInputRef = useRef<HTMLInputElement>(null);

  // ── 可編輯狀態 ─────────────────────────────────────────────────
  const [rows, setRows]         = useState<SummaryRow[]>([]);
  const [totalYears, setTotalYears] = useState<string>('');
  // 追蹤哪些列的 emission_reductions 被手動覆寫
  const [lockedRows, setLockedRows] = useState<Set<string>>(new Set());

  // ── 填入資料 ───────────────────────────────────────────────────
  const initFromDetail = (d: SectionB76Detail) => {
    // 計入期間總年數：永遠以 Section C 的最新設定為準，不沿用已儲存值
    const defaultYears = d.total_years_default ?? d.total_years ?? null;
    setTotalYears(defaultYears != null ? String(defaultYears) : '');

    if (d.rows && d.rows.length > 0) {
      setRows(d.rows);
    } else if (d.section_c_complete && defaultYears && defaultYears > 0) {
      // 自動產生年度列
      setRows(generateRows(d.crediting_period_start, defaultYears));
    } else {
      setRows([]);
    }
    setLockedRows(new Set());
  };

  // ── 載入資料 ───────────────────────────────────────────────────
  const loadData = useCallback(async (projectId: number) => {
    setLoading(true);
    try {
      await validateSession('SectionB76Page');
      const res = await sectionB76Service.getSectionB76ByProject(projectId);
      setDetail(res);
      if (res) initFromDetail(res);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedProjectId) { setDetail(null); setRows([]); loadData(selectedProjectId); }
  }, [selectedProjectId, loadData]);

  // ── 行操作 ────────────────────────────────────────────────────
  const addRow = () => {
    const n = rows.length + 1;
    const startYear = extractYear(detail?.crediting_period_start ?? '');
    setRows(prev => [...prev, {
      id: genId(),
      year_label: startYear ? `Year ${n} (${startYear + n - 1})` : `Year ${n}`,
      baseline_emissions: null, project_emissions: null, leakage_emissions: null, emission_reductions: null,
    }]);
  };

  const deleteRow = (id: string) => {
    setRows(prev => prev.filter(r => r.id !== id));
    setLockedRows(prev => { const s = new Set(prev); s.delete(id); return s; });
  };

  const updateRowField = (id: string, field: keyof SummaryRow, value: any) => {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      const updated = { ...r, [field]: value };
      // emission_reductions 自動計算（除非已鎖定）
      if (field !== 'emission_reductions' && !lockedRows.has(id)) {
        const b = updated.baseline_emissions ?? 0;
        const p = updated.project_emissions  ?? 0;
        const l = updated.leakage_emissions  ?? 0;
        if (updated.baseline_emissions != null || updated.project_emissions != null || updated.leakage_emissions != null) {
          updated.emission_reductions = Math.round((b - p - l) * 100) / 100;
        }
      }
      return updated;
    }));
  };

  const overrideEmissionReductions = (id: string, value: number | null) => {
    setLockedRows(prev => { const s = new Set(prev); if (value != null) s.add(id); else s.delete(id); return s; });
    setRows(prev => prev.map(r => r.id === id ? { ...r, emission_reductions: value } : r));
  };

  // ── 合計與年均值 ──────────────────────────────────────────────
  const sumField = (field: keyof SummaryRow): number | null => {
    const vals = rows.map(r => r[field] as number | null).filter(v => v != null) as number[];
    return vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) * 100) / 100 : null;
  };

  const totalYearsNum = parseInt(totalYears);
  const avgField = (field: keyof SummaryRow): number | null => {
    const s = sumField(field);
    if (s == null || !totalYearsNum || totalYearsNum === 0) return null;
    return Math.round((s / totalYearsNum) * 100) / 100;
  };

  // ── 儲存 ───────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedProjectId) return;
    setSaving(true);
    try {
      const res = await sectionB76Service.saveSectionB76(selectedProjectId, {
        rows,
        total_years: totalYearsNum || detail?.total_years_default || null,
      });
      setDetail(res);
      show(true, t('sectionB76.saveSuccess'));
    } catch { show(false, t('sectionB76.saveFailed')); }
    finally { setSaving(false); }
  };

  // ── 重置 ───────────────────────────────────────────────────────
  const handleReset = async () => {
    if (!selectedProjectId) return;
    try {
      await sectionB76Service.resetSectionB76(selectedProjectId);
      setDetail(prev => prev ? { ...prev, rows: null, total_years: null, b7_6: 0 } : null);
      setRows([]);
      setTotalYears(detail?.total_years_default ? String(detail.total_years_default) : '');
      setLockedRows(new Set());
    } catch { /* ignore */ }
    setResetConfirm(false);
  };

  // ── XLSX 匯入 ──────────────────────────────────────────────────
  const handleImportXlsx = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedProjectId || !e.target.files?.[0]) return;
    setImporting(true);
    try {
      const parsed = await importFromXlsx(selectedProjectId, e.target.files[0]);
      if (parsed.length > 0) {
        setRows(parsed);
        setLockedRows(new Set());
        show(true, isZh ? `已匯入 ${parsed.length} 筆年度資料` : `Imported ${parsed.length} year rows`);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      show(false, detail || (isZh ? '匯入失敗，請確認檔案格式' : 'Import failed. Please check the file format.'));
    } finally {
      setImporting(false);
      if (xlsxInputRef.current) xlsxInputRef.current.value = '';
    }
  };

  const sectionCComplete = detail?.section_c_complete ?? false;
  const hasData = rows.length > 0 || (detail?.total_years != null);
  const showReset = canUpdate && hasData;

  const thStyle: React.CSSProperties = { padding: '8px 10px', background: '#f0f5ff', fontWeight: 600, fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '2px solid #d0d8f0' };
  const thLeftStyle: React.CSSProperties = { ...thStyle, textAlign: 'left', width: 130 };
  const tdStyle: React.CSSProperties = { padding: '4px 8px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' };
  const tdReadonlyStyle: React.CSSProperties = { ...tdStyle, textAlign: 'right', fontSize: 12, color: '#555', background: '#fafafa' };
  const footerRowStyle: React.CSSProperties = { background: '#f8faff' };

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="page-header">
        <FunctionPageHeader funcCode="section_b7_6" />
        {showReset && (
          <div className="page-header-right">
            <button onClick={() => setResetConfirm(true)}
              style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e53935', background: '#fff', color: '#e53935', cursor: 'pointer', fontWeight: 500, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
              title={t('sectionB76.resetTitle')}>
              🗑 {t('sectionB76.resetData')}
            </button>
          </div>
        )}
      </div>

      {/* 未選專案 */}
      {!selectedProjectId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>
          {t('sectionB76.selectProject')}
        </div>
      ) : loading ? (
        <div className="loading">{t('sectionB76.loading')}</div>
      ) : !sectionCComplete ? (
        /* Section C 未完成 */
        <div style={{ padding: '32px 24px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 10, maxWidth: 640 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#d46b08', marginBottom: 8 }}>
            ⚠ {isZh ? '請先完成 C.計入期說明' : 'Please complete Section C first'}
          </div>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 16, lineHeight: 1.7 }}>
            {isZh
              ? '填寫本節資料前，請先完成「C.計入期說明（Section C）」，以便自動帶入計入期年數與起始年度。'
              : 'Please complete "C. Start date, crediting period type and duration (Section C)" before entering data here, so that the crediting period and start year can be auto-populated.'}
          </div>
          <a href="#" onClick={e => { e.preventDefault(); window.history.pushState({}, '', '/section_c'); window.dispatchEvent(new PopStateEvent('popstate')); }}
            style={{ display: 'inline-block', padding: '7px 18px', borderRadius: 6, background: '#fa8c16', color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
            → {isZh ? '前往 C.計入期說明' : 'Go to Section C'}
          </a>
        </div>
      ) : (
        /* 主內容：摘要表格 */
        <div>
          {/* 新增年度 / 匯入 XLSX 按鈕 */}
          {canUpdate && (
            <div style={{ marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
              <button onClick={addRow}
                style={{ padding: '7px 18px', fontSize: 13, borderRadius: 6, border: '1px solid #4A90E2', background: '#4A90E2', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                + {t('sectionB76.addYear')}
              </button>
              <button onClick={() => xlsxInputRef.current?.click()} disabled={importing}
                style={{ padding: '7px 18px', fontSize: 13, borderRadius: 6, border: '1px solid #52c41a', background: importing ? '#ccc' : '#52c41a', color: '#fff', cursor: importing ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                📥 {importing ? (isZh ? '匯入中…' : 'Importing…') : (isZh ? '從試算表匯入' : 'Import from XLSX')}
              </button>
              <input ref={xlsxInputRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleImportXlsx} />
            </div>
          )}

          {/* 摘要表格 */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 }}>
              <thead className="table-header-dark-green">
                <tr>
                  <th style={thLeftStyle}>{isZh ? '年份' : 'Year'}</th>
                  <th style={thStyle}>{isZh ? '基準線排放／移除量' : 'Baseline emissions/removals'}<br /><span style={{ fontWeight: 400, fontSize: 11 }}>(tCO₂e)</span></th>
                  <th style={thStyle}>{isZh ? '專案排放／移除量' : 'Project emissions/removals'}<br /><span style={{ fontWeight: 400, fontSize: 11 }}>(tCO₂e)</span></th>
                  <th style={thStyle}>{isZh ? '洩漏排放量' : 'Leakage emissions'}<br /><span style={{ fontWeight: 400, fontSize: 11 }}>(tCO₂e)</span></th>
                  <th style={thStyle}>{isZh ? '排放減量／淨移除量' : 'Emission reductions/Net removals'}<br /><span style={{ fontWeight: 400, fontSize: 11 }}>(tCO₂e)</span></th>
                  {canUpdate && <th style={{ ...thStyle, width: 50, textAlign: 'center' }}></th>}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={canUpdate ? 6 : 5} style={{ textAlign: 'center', padding: '24px', color: '#aaa', fontSize: 13 }}>
                      {t('sectionB76.noData')}
                    </td>
                  </tr>
                )}
                {rows.map(row => (
                  <tr key={row.id}>
                    <td style={tdStyle}>
                      {canUpdate ? (
                        <input value={row.year_label} onChange={e => updateRowField(row.id, 'year_label', e.target.value)}
                          style={{ width: '100%', padding: '3px 6px', border: '1px solid #e0e0e0', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }} />
                      ) : <span style={{ fontSize: 12 }}>{row.year_label}</span>}
                    </td>
                    <td style={tdStyle}>
                      <NumberCell value={row.baseline_emissions} disabled={!canUpdate}
                        onChange={v => updateRowField(row.id, 'baseline_emissions', v)} />
                    </td>
                    <td style={tdStyle}>
                      <NumberCell value={row.project_emissions} disabled={!canUpdate}
                        onChange={v => updateRowField(row.id, 'project_emissions', v)} />
                    </td>
                    <td style={tdStyle}>
                      <NumberCell value={row.leakage_emissions} disabled={!canUpdate}
                        onChange={v => updateRowField(row.id, 'leakage_emissions', v)} />
                    </td>
                    <td style={tdStyle}>
                      <NumberCell value={row.emission_reductions} disabled={!canUpdate}
                        highlighted={lockedRows.has(row.id)}
                        onChange={v => overrideEmissionReductions(row.id, v)} />
                    </td>
                    {canUpdate && (
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <button onClick={() => deleteRow(row.id)}
                          style={{ padding: '2px 8px', fontSize: 11, borderRadius: 4, border: '1px solid #ff7875', background: '#fff', color: '#ff4d4f', cursor: 'pointer' }}>✕</button>
                      </td>
                    )}
                  </tr>
                ))}

                {/* Total 列 */}
                {rows.length > 0 && (
                  <tr style={footerRowStyle}>
                    <td style={{ ...tdStyle, fontWeight: 700, fontSize: 12 }}>{isZh ? '合計 Total' : 'Total'}</td>
                    {(['baseline_emissions','project_emissions','leakage_emissions','emission_reductions'] as const).map(f => (
                      <td key={f} style={tdReadonlyStyle}>{fmtNum(sumField(f)) || '—'}</td>
                    ))}
                    {canUpdate && <td style={tdStyle}></td>}
                  </tr>
                )}

                {/* Total number of years 列（唯讀，來自 Section C） */}
                <tr style={footerRowStyle}>
                  <td style={{ ...tdStyle, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {isZh ? '計入期間總年數' : 'Total number of years in the crediting period'}
                  </td>
                  <td colSpan={4} style={{ ...tdReadonlyStyle, textAlign: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{totalYears || '—'}</span>
                    <span style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>
                      ({isZh ? '依 Section C 計入期期間' : 'from Section C crediting period duration'})
                    </span>
                  </td>
                  {canUpdate && <td style={tdStyle}></td>}
                </tr>

                {/* Annual average 列 */}
                {rows.length > 0 && (
                  <tr style={footerRowStyle}>
                    <td style={{ ...tdStyle, fontSize: 12, fontWeight: 600 }}>
                      {isZh ? '年度平均' : 'Annual average over the crediting period'}
                    </td>
                    {(['baseline_emissions','project_emissions','leakage_emissions','emission_reductions'] as const).map(f => (
                      <td key={f} style={tdReadonlyStyle}>{fmtNum(avgField(f)) || '—'}</td>
                    ))}
                    {canUpdate && <td style={tdStyle}></td>}
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 儲存按鈕 */}
          {canUpdate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '9px 28px', fontSize: 14, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#4A90E2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                {saving ? t('sectionB76.saving') : t('sectionB76.save')}
              </button>
              {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
            </div>
          )}
        </div>
      )}

      {/* Reset confirm */}
      {resetConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', minWidth: 360, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('sectionB76.resetTitle')}</div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 20, whiteSpace: 'pre-line' }}>{t('sectionB76.resetConfirm')}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setResetConfirm(false)}
                style={{ padding: '6px 18px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>
                {t('sectionB76.cancel')}
              </button>
              <button onClick={handleReset}
                style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: '#ff4d4f', color: '#fff', cursor: 'pointer' }}>
                {t('sectionB76.reset')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionB76Page;
